import { useState, useEffect } from "react";
import {
  Gift,
  Send,
  Mail,
  CheckCircle2,
  Clock,
  Sparkles,
  Users,
  DollarSign,
  Share2,
  PartyPopper,
  Shield,
  Download,
  CheckSquare,
  Square,
  AlertTriangle,
  Search,
  Filter,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useReferral } from "../hooks/useReferral";
import ReferralDashboard from "../components/ReferralDashboard";
import RewardsTracker from "../components/RewardsTracker";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import SkeletonTable from "../components/SkeletonTable";

import { useTranslation } from 'react-i18next';
function StepCard({ number, title, description, icon: Icon }) {
  return (
    <div className="relative border border-zinc-800 bg-zinc-950/30 p-6 group hover:border-amber-500/30 transition-colors">
      <div className="absolute -top-3 -left-3 w-8 h-8 bg-amber-500 text-black font-mono text-sm font-bold flex items-center justify-center">
        {number}
      </div>
      <Icon className="w-6 h-6 text-amber-400 mb-3" />
      <h3 className="font-display text-lg text-white mb-2">{title}</h3>
      <p className="font-mono text-[12px] text-zinc-500 leading-relaxed">{description}</p>
    </div>
  );
}

function AdminPanel({ referrals, fraudFlags, onUpdateStatus, onBulkUpdate, onExport }) {
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFraudOnly, setShowFraudOnly] = useState(false);

  const fraudIds = new Set(fraudFlags.map((f) => f.referralId));

  const filtered = referrals.filter((r) => {
    if (search && !r.email.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (showFraudOnly && !fraudIds.has(r.id)) return false;
    return true;
  });

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    if (selected.length === filtered.length) {
      setSelected([]);
    } else {
      setSelected(filtered.map((r) => r.id));
    }
  };

  const handleBulkApprove = () => {
    onBulkUpdate(selected, "approved");
    toast.success(`${selected.length} referrals approved`);
    setSelected([]);
  };

  const handleBulkReject = () => {
    onBulkUpdate(selected, "rejected");
    toast.success(`${selected.length} referrals rejected`);
    setSelected([]);
  };

  return (
    <div className="space-y-4">
      {/* Admin Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-400" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400">{t('referral.admin_panel')}</span>
        </div>
        <button
          onClick={onExport}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-700 hover:border-zinc-500 font-mono text-[10px] text-zinc-400 transition-colors"
        >
          <Download className="w-3 h-3" />
          Export CSV
        </button>
      </div>

      {/* Fraud Alerts */}
      {fraudFlags.length > 0 && (
        <div className="border border-red-500/30 bg-red-500/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="font-mono text-[11px] text-red-400 uppercase tracking-wider">
              {fraudFlags.length} Fraud Alert{fraudFlags.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="space-y-1">
            {fraudFlags.slice(0, 5).map((f, i) => (
              <div key={i} className="font-mono text-[10px] text-red-400/70">
                {f.type === "duplicate_email" && `Duplicate email: ${f.email}`}
                {f.type === "duplicate_ip" && `Duplicate IP: ${f.ip}`}
                {f.type === "self_referral" && `Self-referral detected: ${f.email}`}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-wrap gap-2">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('referral.search_by_email')}
            className="w-full pl-9 pr-3 py-2 bg-zinc-950/60 border border-zinc-800 font-mono text-[12px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 bg-zinc-950/60 border border-zinc-800 font-mono text-[12px] text-zinc-400 focus:outline-none focus:border-amber-500/50"
        >
          <option value="all">{t('ab_testing.all_status')}</option>
          <option value="pending">{t('referral.status_pending')}</option>
          <option value="approved">{t('referral.status_approved')}</option>
          <option value="converted">{t('referral.stat_converted')}</option>
          <option value="rejected">{t('referral.status_rejected')}</option>
          <option value="expired">{t('referral.expired')}</option>
        </select>
        <button
          onClick={() => setShowFraudOnly(!showFraudOnly)}
          className={`flex items-center gap-1.5 px-3 py-2 border font-mono text-[11px] transition-colors ${showFraudOnly ? "border-red-500/50 bg-red-500/10 text-red-400" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}
        >
          <Filter className="w-3 h-3" />
          Fraud Only
        </button>
      </div>

      {/* Bulk Actions */}
      {selected.length > 0 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 border border-amber-500/20 bg-amber-500/5">
          <span className="font-mono text-[11px] text-amber-400">{selected.length} selected</span>
          <button onClick={handleBulkApprove} className="font-mono text-[10px] text-emerald-400 hover:text-emerald-300 uppercase tracking-wider">{t('referral.approve_all')}</button>
          <button onClick={handleBulkReject} className="font-mono text-[10px] text-red-400 hover:text-red-300 uppercase tracking-wider">{t('referral.reject_all')}</button>
          <button onClick={() => setSelected([])} className="font-mono text-[10px] text-zinc-500 hover:text-zinc-400 ml-auto">
            Clear
          </button>
        </div>
      )}

      {/* Table - Desktop */}
      <div className="hidden md:block border border-zinc-800 overflow-x-auto">
        <div className="grid grid-cols-[40px_1fr_100px_100px_80px] gap-2 p-3 border-b border-zinc-800 bg-zinc-950/50">
          <button onClick={toggleSelectAll} className="flex items-center justify-center">
            {selected.length === filtered.length && filtered.length > 0 ? (
              <CheckSquare className="w-4 h-4 text-amber-400" />
            ) : (
              <Square className="w-4 h-4 text-zinc-600" />
            )}
          </button>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('referral.email')}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('referral.status')}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('referral.date')}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('referral.actions')}</span>
        </div>

        {filtered.length === 0 ? (
          <div className="p-8 text-center">
            <p className="font-mono text-[12px] text-zinc-600">{t('referral.no_referrals_found')}</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800 max-h-[400px] overflow-y-auto">
            {filtered.map((r) => {
              const isFraud = fraudIds.has(r.id);
              return (
                <div key={r.id} className={`grid grid-cols-[40px_1fr_100px_100px_80px] gap-2 p-3 items-center ${isFraud ? "bg-red-500/5" : ""}`}>
                  <button onClick={() => toggleSelect(r.id)} className="flex items-center justify-center">
                    {selected.includes(r.id) ? (
                      <CheckSquare className="w-4 h-4 text-amber-400" />
                    ) : (
                      <Square className="w-4 h-4 text-zinc-600" />
                    )}
                  </button>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-[11px] text-zinc-400 truncate">{r.email}</span>
                    {isFraud && <AlertTriangle className="w-3 h-3 text-red-400 shrink-0" />}
                  </div>
                  <span className={`font-mono text-[10px] uppercase tracking-wider ${
                    r.status === "converted" ? "text-emerald-400" :
                    r.status === "pending" ? "text-amber-400" :
                    r.status === "rejected" ? "text-red-400" :
                    r.status === "expired" ? "text-zinc-500" :
                    "text-blue-400"
                  }`}>
                    {r.status}
                  </span>
                  <span className="font-mono text-[10px] text-zinc-600">
                    {new Date(r.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </span>
                  <div className="flex gap-1">
                    {r.status === "pending" && (
                      <>
                        <button onClick={() => onUpdateStatus(r.id, "approved")} className="font-mono text-[9px] text-emerald-400 hover:text-emerald-300" title={t('referral.approve')}>
                          ✓
                        </button>
                        <button onClick={() => onUpdateStatus(r.id, "rejected")} className="font-mono text-[9px] text-red-400 hover:text-red-300" title={t('referral.reject')}>
                          <X className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Table - Mobile Card Layout */}
      <div className="md:hidden space-y-2">
        {filtered.length === 0 ? (
          <div className="p-8 text-center border border-zinc-800">
            <p className="font-mono text-[12px] text-zinc-600">{t('referral.no_referrals_found')}</p>
          </div>
        ) : (
          filtered.map((r) => {
            const isFraud = fraudIds.has(r.id);
            return (
              <div key={r.id} className={`border border-zinc-800 p-3 space-y-2 ${isFraud ? "bg-red-500/5 border-red-500/30" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <button onClick={() => toggleSelect(r.id)} className="shrink-0">
                      {selected.includes(r.id) ? (
                        <CheckSquare className="w-4 h-4 text-amber-400" />
                      ) : (
                        <Square className="w-4 h-4 text-zinc-600" />
                      )}
                    </button>
                    <span className="font-mono text-[11px] text-zinc-400 truncate">{r.email}</span>
                    {isFraud && <AlertTriangle className="w-3 h-3 text-red-400 shrink-0" />}
                  </div>
                  <span className={`font-mono text-[10px] uppercase tracking-wider shrink-0 ${
                    r.status === "converted" ? "text-emerald-400" :
                    r.status === "pending" ? "text-amber-400" :
                    r.status === "rejected" ? "text-red-400" :
                    r.status === "expired" ? "text-zinc-500" :
                    "text-blue-400"
                  }`}>
                    {r.status}
                  </span>
                </div>
                <div className="flex items-center justify-between pl-6">
                  <span className="font-mono text-[10px] text-zinc-600">
                    {new Date(r.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </span>
                  {r.status === "pending" && (
                    <div className="flex gap-2">
                      <button onClick={() => onUpdateStatus(r.id, "approved")} className="font-mono text-[10px] text-emerald-400 hover:text-emerald-300 uppercase tracking-wider">
                        Approve
                      </button>
                      <button onClick={() => onUpdateStatus(r.id, "rejected")} className="font-mono text-[10px] text-red-400 hover:text-red-300 uppercase tracking-wider">
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function Referral() {
  const { t } = useTranslation();
  const {
    referralLink,
    referralCode,
    referrals,
    stats,
    tierInfo,
    leaderboard,
    fraudFlags,
    sendInvite,
    simulateConversion,
    updateReferralStatus,
    bulkUpdateStatus,
    copyLink,
    shareWhatsApp,
    shareTelegram,
    shareTwitter,
    shareLinkedIn,
    shareEmail,
    exportCSV,
    rewardHistory,
  } = useReferral();

  const [email, setEmail] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [adminLoading, setAdminLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setAdminLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  const handleCopy = () => {
    copyLink();
    toast.success("Link copied to clipboard!");
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 2000);
  };

  const handleSendInvite = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter an email address.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    sendInvite(email.trim());
    setEmail("");
    toast.success("Invite sent!", {
      description: `${email} will receive your referral link.`,
    });
  };

  const tabs = [
    { id: "dashboard", label: "Dashboard" },
    { id: "rewards", label: "Rewards" },
    { id: "admin", label: "Admin" },
  ];

  return (
    <>
      <SEOHead
        title={t('referral.referral_program_max_employee')}
        description={t('referral.refer_friends_to_max_employee_and_earn_up_to_50')}
      />
      <Header />

      <main id="main-content" className="min-h-screen bg-zinc-950">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-zinc-800">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-transparent" />
          <div className="relative max-w-5xl mx-auto px-4 py-10 sm:py-16 text-center">
            {showConfetti && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <PartyPopper className="w-12 h-12 text-amber-400 animate-bounce" />
              </div>
            )}
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 mb-6">
              <Gift className="w-4 h-4 text-amber-400" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400">{t('referral.referral_program')}</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl text-white mb-4">
              Give $25, Get $25
            </h1>
            <p className="font-mono text-[14px] text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              Share Max Employee with friends. When they sign up, you both get $25 in credits.
              Earn more with tiered rewards and milestone bonuses.
            </p>
          </div>
        </section>

        {/* Quick Stats Bar */}
        <section className="border-b border-zinc-800 bg-zinc-950/50">
          <div className="max-w-5xl mx-auto px-4 py-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="font-display text-lg sm:text-xl md:text-2xl text-amber-400">{stats.totalReferrals}</div>
                <div className="font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{t('referral.total_referrals')}</div>
              </div>
              <div className="text-center">
                <div className="font-display text-lg sm:text-xl md:text-2xl text-emerald-400">{stats.conversionRate}%</div>
                <div className="font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{t('referral.conversion_rate')}</div>
              </div>
              <div className="text-center">
                <div className="font-display text-lg sm:text-xl md:text-2xl text-white">${stats.credits}</div>
                <div className="font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{t('referral.credits_earned')}</div>
              </div>
              <div className="text-center">
                <div className="font-display text-lg sm:text-xl md:text-2xl text-violet-400">{tierInfo.current}</div>
                <div className="font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{t('referral.current_tier')}</div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="border-b border-zinc-800">
          <div className="max-w-5xl mx-auto px-4 py-8 sm:py-14">
            <div className="text-center mb-10">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">{t('referral.how_it_works')}</span>
              <h2 className="font-display text-2xl text-white mt-2">3 Simple Steps</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <StepCard number="1" title={t('referral.share_your_link')} description={t('referral.copy_your_unique_referral_link_or_qr_code_and')} icon={Share2} />
              <StepCard number="2" title={t('referral.friend_signs_up')} description={t('referral.your_friend_clicks_the_link_and_creates_their_max')} icon={Users} />
              <StepCard number="3" title={t('referral.both_earn_rewards')} description={t('referral.once_they_activate_you_both_receive_credits_earn')} icon={DollarSign} />
            </div>
          </div>
        </section>

        {/* Invite Form */}
        <section className="border-b border-zinc-800 bg-zinc-950/30">
          <div className="max-w-5xl mx-auto px-4 py-14">
            <div className="text-center mb-8">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">{t('referral.send_invites')}</span>
              <h2 className="font-display text-2xl text-white mt-2">{t('referral.invite_friends_via_email')}</h2>
            </div>
            <div className="max-w-lg mx-auto">
              <form onSubmit={handleSendInvite} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="friend@email.com"
                  className="w-full sm:flex-1 px-4 py-2.5 bg-zinc-950/60 border border-zinc-800 font-mono text-[13px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50 transition-colors"
                  aria-label={t('referral.friends_email_address')}
                />
                <button
                  type="submit"
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-amber-500 text-black font-mono text-[11px] uppercase tracking-wider hover:bg-amber-400 transition-colors"
                >
                  <Send className="w-3.5 h-3.5" />
                  Send
                </button>
              </form>
              {referrals.length > 0 && (
                <div className="mt-4 border border-zinc-800 divide-y divide-zinc-800">
                  {referrals.slice(0, 5).map((r) => (
                    <div key={r.id} className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Mail className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                        <span className="font-mono text-[11px] text-zinc-400 truncate">{r.email}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`font-mono text-[9px] uppercase tracking-wider ${r.status === "converted" ? "text-emerald-400" : r.status === "pending" ? "text-amber-400" : "text-zinc-500"}`}>
                          {r.status === "converted" ? <CheckCircle2 className="w-3 h-3 inline" /> : <Clock className="w-3 h-3 inline" />} {r.status}
                        </span>
                        {r.status === "pending" && (
                          <button onClick={() => simulateConversion()} className="font-mono text-[9px] text-amber-500 hover:text-amber-400 uppercase tracking-wider" title={t('referral.simulate_conversion_demo')}>
                            Convert
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="font-mono text-[10px] text-zinc-600 mt-2 text-center">
                Tip: Click "Convert" on a pending invite to simulate a friend signing up (demo mode).
              </p>
            </div>
          </div>
        </section>

        {/* Tabs: Dashboard / Rewards / Admin */}
        <section className="border-b border-zinc-800">
          <div className="max-w-5xl mx-auto px-4 py-14">
            {/* Tab Navigation */}
            <div className="flex overflow-x-auto gap-1 mb-8 border-b border-zinc-800">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`shrink-0 px-5 py-3 font-mono text-[11px] uppercase tracking-wider transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? "border-amber-500 text-amber-400"
                      : "border-transparent text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {tab.label}
                  {tab.id === "admin" && fraudFlags.length > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-red-500/20 text-red-400 text-[9px]">{fraudFlags.length}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === "dashboard" && (
              <ReferralDashboard
                referralLink={referralLink}
                referralCode={referralCode}
                stats={stats}
                tierInfo={tierInfo}
                leaderboard={leaderboard}
                onCopyLink={copyLink}
                onShareWhatsApp={shareWhatsApp}
                onShareTelegram={shareTelegram}
                onShareTwitter={shareTwitter}
                onShareLinkedIn={shareLinkedIn}
                onShareEmail={shareEmail}
              />
            )}

            {activeTab === "rewards" && (
              <RewardsTracker
                stats={stats}
                tierInfo={tierInfo}
                rewardHistory={rewardHistory}
              />
            )}

            {activeTab === "admin" && (
              adminLoading ? (
                <SkeletonTable rows={8} columns={4} />
              ) : (
                <AdminPanel
                  referrals={referrals}
                  fraudFlags={fraudFlags}
                  onUpdateStatus={updateReferralStatus}
                  onBulkUpdate={bulkUpdateStatus}
                  onExport={exportCSV}
                />
              )
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-amber-500/5 to-transparent" />
          <div className="relative max-w-2xl mx-auto px-4 py-10 sm:py-16 text-center">
            <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-4" />
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-3">{t('referral.start_earning_today')}</h2>
            <p className="font-mono text-[13px] text-zinc-500 mb-6">
              Share your link with one friend and you're already $25 closer to your next upgrade.
            </p>
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-black font-mono text-sm uppercase tracking-wider hover:bg-amber-400 transition-colors"
            >
              <Gift className="w-4 h-4" />
              Copy Your Link
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
